/**
 * Simulazione della run — TIMESTEP FISSO, DETERMINISTICA.
 *
 * Modello "pompata" (Tiny Wings):
 *   held=true  -> gravità forte: ti incolli al pendio e acceleri in discesa (dive)
 *   held=false -> gravità debole: plani, le creste ti lanciano in volo
 * La skill è nel ritmo: premere sulla discesa, rilasciare in cresta per volare
 * oltre e riatterrare in pendenza. Atterrare storti = wipeout (tempo perso).
 *
 * `step()` è PURA rispetto agli input (stato + terreno + held). Nessun accesso a
 * Date.now/Math.random/trig → stesso seed+input = stesso stato, sempre e ovunque.
 * Questo è ciò che rende i ghost gratis e il verify-replay server-side possibile.
 */

import type { Terrain } from './terrain';
import { len2 } from './dmath';
import * as C from './constants';

export interface SimState {
  t: number; // tempo di gara (s)
  x: number; // posizione orizzontale (px-mondo)
  y: number; // posizione verticale (px-mondo, giù = +)
  vx: number;
  vy: number;
  grounded: boolean;
  wipeTimer: number; // >0 = in wipeout (capriola)
  finished: boolean;
  finishT: number; // tempo al traguardo (s)
  steps: number; // contatore di step (safety)
}

export function initSim(terrain: Terrain): SimState {
  const x = C.START_X;
  return {
    t: 0,
    x,
    y: terrain.heightAt(x),
    vx: C.START_VX,
    vy: 0,
    grounded: true,
    wipeTimer: 0,
    finished: false,
    finishT: 0,
    steps: 0,
  };
}

function clampSpeed(s: SimState): void {
  const sp = len2(s.vx, s.vy);
  if (sp > C.MAX_SPEED) {
    const k = C.MAX_SPEED / sp;
    s.vx *= k;
    s.vy *= k;
  }
}

/** Un singolo passo di simulazione a dt fisso. Muta `s`. */
export function step(s: SimState, terrain: Terrain, held: boolean): void {
  if (s.finished) return;
  const dt = C.SIM_DT;
  s.t += dt;
  s.steps++;

  const wiping = s.wipeTimer > 0;
  const press = held && !wiping;

  // 1) Gravità (la leva della pompata).
  const g = press ? C.GRAV_PRESS : C.GRAV_RELEASE;
  s.vy += g * dt;

  // 2) Integrazione esplicita.
  s.x += s.vx * dt;
  s.y += s.vy * dt;

  // 3) Contatto col suolo.
  const gy = terrain.heightAt(s.x);
  if (s.y >= gy) {
    const { tx, ty } = terrain.slopeAt(s.x);
    const nx = -ty; // normale al pendio
    const ny = tx;
    const along = s.vx * tx + s.vy * ty; // componente lungo il pendio
    const intoGround = s.vx * nx + s.vy * ny; // componente normale (impatto)
    const speed = len2(s.vx, s.vy);

    if (!s.grounded && !wiping) {
      // Atterraggio dall'aria: se storto e veloce -> wipeout.
      const align = speed > 1 ? along / speed : 1;
      const impact = intoGround > 0 ? intoGround : -intoGround;
      if (align < C.WIPE_ALIGN && impact > C.WIPE_MIN_IMPACT) {
        const keep = Math.max(C.MIN_VX, (along > 0 ? along : 0) * C.WIPE_SPEED_KEEP);
        s.wipeTimer = C.WIPE_TIME;
        s.vx = tx * keep;
        s.vy = ty * keep;
        s.y = gy;
        s.grounded = true;
        clampSpeed(s);
        if (s.x >= C.FINISH_X) finish(s);
        return;
      }
    }

    // Proiezione sul pendio: si conserva la velocità tangenziale, si annulla
    // la componente normale. La gravità applicata sopra, proiettata, dà
    // l'accelerazione in discesa e la frenata in salita (naturale, senza trig).
    const fric = wiping ? 0.9 : C.GROUND_FRICTION;
    let kept = along * (1 - fric * dt);
    // Spinta della pompata: premi in discesa = boost, premi in salita = freno.
    if (press) kept += C.PUMP_ACCEL * ty * dt;
    s.vx = tx * kept;
    s.vy = ty * kept;
    s.y = gy;
    s.grounded = true;
  } else {
    // In aria.
    s.vx *= 1 - C.AIR_DRAG * dt;
    s.grounded = false;
  }

  // 4) Anti-stallo + cap. Il pavimento è INCONDIZIONATO: non si va mai indietro,
  //    nemmeno in wipeout (dove MIN_VX resta comunque lentissimo = tempo perso).
  if (s.vx < C.MIN_VX) s.vx = C.MIN_VX;
  clampSpeed(s);
  if (s.wipeTimer > 0) {
    s.wipeTimer -= dt;
    if (s.wipeTimer < 0) s.wipeTimer = 0;
  }

  // 5) Traguardo.
  if (s.x >= C.FINISH_X) finish(s);
}

function finish(s: SimState): void {
  s.x = C.FINISH_X;
  s.finished = true;
  s.finishT = s.t;
}

/** Copia leggera dello stato (per l'interpolazione di render tra due step). */
export function cloneState(s: SimState): SimState {
  return { ...s };
}
